import { ChangeEvent, FC, MouseEvent, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Paper, Table, TableBody, TableContainer, TableFooter, TableHead, TableRow } from "@mui/material";

import { TrainArrangementSortColumn } from "@framework/types";
import { SortDirection } from "@framework/types-collection";

import { SortButton } from "../../../../../../../components/common/buttons/sort-button";
import { StyledTablePagination } from "../../../ui/table-pagination";
import { formatDateHumanReadable } from "../../../../../../../utils";
import { useScreenSize } from "../../../../../../../utils/screen";
import { ITrainArrangementResponse } from "../interfaces";
import { TrainArrangementRow } from "./row";
import { StyledHeaderTableCell } from "./styled";

export interface ITrainArrangementTableProps {
  trainArrangements: ITrainArrangementResponse[];
  setIsLoading: (value: boolean) => void;
  handleSort: (value: TrainArrangementSortColumn) => () => void;
  sortColumn: TrainArrangementSortColumn;
  sortOrder: SortDirection;
}

export const TrainArrangementsTable: FC<ITrainArrangementTableProps> = props => {
  const { handleSort, sortColumn, sortOrder, trainArrangements, setIsLoading } = props;

  const { isExtraLargeScreen } = useScreenSize();
  const { formatMessage } = useIntl();
  const headersId = "pages.scheme.trainArrangements.headers";

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const rows: any[] = useMemo(
    () =>
      trainArrangements.map(trainArrangement => {
        const { idTrainArrangement, StartDate, EndDate, Type, Description, actionsExists } = trainArrangement;

        return {
          id: idTrainArrangement,
          name: Type,
          endDate: formatDateHumanReadable(EndDate),
          startDate: formatDateHumanReadable(StartDate),
          description: Description,
          actionsExists: !!actionsExists,
        };
      }),
    [trainArrangements],
  );

  const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (isExtraLargeScreen) {
      setPage(0);
      setRowsPerPage(15);
    } else {
      setPage(0);
      setRowsPerPage(5);
    }
  }, [isExtraLargeScreen]);

  return (
    <TableContainer
      elevation={0}
      component={Paper}
      sx={{
        flex: 1,
        minHeight: 0,
        maxHeight: { xs: 520, xl: 1000, xxl: 1200 },
        overflow: "auto",
        borderRadius: "8px",
        border: "1px solid #EBEBF7",
      }}
    >
      <Table
        sx={{ minWidth: 500, width: "100%", tableLayout: "fixed", position: "relative" }}
        aria-label="Machine Arrangements table"
        stickyHeader
      >
        <TableHead>
          <TableRow
            sx={{
              "& .MuiTableCell-root": {
                borderRight: "1px solid #EBEBF7",
                backgroundColor: "#FAFAFF",
                borderColor: "#EBEBF7",
                "&:last-of-type": {
                  borderRight: "none",
                },
              },
            }}
          >
            <StyledHeaderTableCell
              width={48}
              component="th"
              scope="row"
              sx={{ maxWidth: 48, minWidth: 48, width: 48, p: 0 }}
            />
            <StyledHeaderTableCell component="th" scope="row" sx={{ width: "15%" }}>
              {formatMessage({ id: `${headersId}.startDate` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainArrangementSortColumn.StartDate)}
                isActive={sortColumn === TrainArrangementSortColumn.StartDate}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell component="th" scope="row" sx={{ width: "15%" }}>
              {formatMessage({ id: `${headersId}.endDate` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainArrangementSortColumn.EndDate)}
                isActive={sortColumn === TrainArrangementSortColumn.EndDate}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell component="th" scope="row" sx={{ width: "15%" }}>
              {formatMessage({ id: `${headersId}.name` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainArrangementSortColumn.Type)}
                isActive={sortColumn === TrainArrangementSortColumn.Type}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell component="th" scope="row" sx={{ width: "15%" }}>
              {formatMessage({ id: `${headersId}.object` })}
            </StyledHeaderTableCell>
            <StyledHeaderTableCell component="th" scope="row" sx={{ width: "15%" }}>
              {formatMessage({ id: `${headersId}.description` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainArrangementSortColumn.Description)}
                isActive={sortColumn === TrainArrangementSortColumn.Description}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell component="th" scope="row" width={48} sx={{ width: 48 }}>
              {formatMessage({ id: `${headersId}.report` })}
            </StyledHeaderTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows).map(row => (
            <TrainArrangementRow key={row.id} row={row} setIsLoading={setIsLoading} />
          ))}
        </TableBody>
        {rows.length > rowsPerPage ? (
          <TableFooter>
            <TableRow
              sx={{
                "& .MuiToolbar-root.MuiTablePagination-toolbar": { minHeight: 30, fontSize: 12 },
                "& .MuiToolbar-root.MuiTablePagination-toolbar p": { fontSize: 12, m: 0 },
                "& .MuiToolbar-root .MuiButtonBase-root": { padding: 0.5 },
              }}
            >
              <StyledTablePagination
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                page={page}
                rowsLength={rows.length}
                rowsPerPage={rowsPerPage}
                colSpan={7}
              />
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </TableContainer>
  );
};
